import { createContext, useContext, useEffect, useRef, useState } from "react";
import Axios from "../Axios";
import $ from "jquery";

const FutureContext = createContext(null);

const normalizeFutureSymbol = (symbol = "") =>
  String(symbol).replace(/[_/]/g, "").toUpperCase();

const chunkTopics = (topics, size = 10) => {
  const chunks = [];

  for (let i = 0; i < topics.length; i += size) {
    chunks.push(topics.slice(i, i + size));
  }

  return chunks;
};

const formatMarketValue = (rawValue) => {
  const value = Number(rawValue);
  if (!Number.isFinite(value)) return "0.00000";

  if (typeof rawValue === "string" && rawValue.includes(".")) {
    const decimals = Math.min(rawValue.split(".")[1].length, 8);
    return value.toFixed(decimals);
  }

  if (Math.abs(value) >= 1000) return value.toFixed(2);
  if (Math.abs(value) >= 1) return value.toFixed(4);
  return value.toFixed(5);
};

const FutureProvider = ({ children }) => {
  const futureSocketRef = useRef(null);
  const futurePriceRef = useRef({});
  const tvWidgetRef = useRef({});
  const lineRefs = useRef({}); // 🟩 Store TP/SL/Bet line IDs per chart
  const [symbols, setSymbols] = useState([]);

  const getSymbols = async () => {
    try {
      const { data } = await Axios.post(
        "/getPairsByAdmin",
        { type: "future" },
        {
          headers: {
            Authorization: localStorage.getItem("nzanzi"),
          },
        },
      );

      if (data?.success) {
        const nextSymbols = [
          ...new Set(
            (data?.result || [])
              .map((item) => normalizeFutureSymbol(item?.pair_symbol))
              .filter(Boolean),
          ),
        ];
        setSymbols(nextSymbols);
      } else {
        setSymbols([]);
      }
    } catch (error) {
      setSymbols([]);
      console.log("Future symbol fetch error:", error);
    }
  };

  const upsertTicker = (message) => {
    const payload = message?.data || message;
    const symbol = normalizeFutureSymbol(
      payload?.s || message?.stream?.split("@")?.[0],
    );

    if (!symbol) return;

    const existingTicker = futurePriceRef.current[symbol] || { symbol };

    const rawAsk = payload?.a ?? payload?.askPrice ?? payload?.ask;
    const rawBid = payload?.b ?? payload?.bidPrice ?? payload?.bid;
    const rawLast = payload?.c ?? payload?.lastPrice ?? payload?.price;
    const rawMark = rawLast;

    const ask =
      rawAsk != null ? Number(rawAsk) : Number(existingTicker.ask || 0);
    const bid =
      rawBid != null ? Number(rawBid) : Number(existingTicker.bid || 0);
    const lastPrice =
      rawLast != null
        ? Number(rawLast)
        : Number(existingTicker.midprice || existingTicker.markPrice || 0);
    const markPrice =
      rawMark != null
        ? Number(rawMark)
        : Number(existingTicker.markPrice || lastPrice || 0);
    const midprice =
      lastPrice || (ask && bid ? (ask + bid) / 2 : markPrice || 0);
    const spread = ask && bid ? ask - bid : 0;
    const displayAsk = rawAsk ?? (ask > 0 ? ask : midprice);
    const displayBid = rawBid ?? (bid > 0 ? bid : midprice);
    const timestamp = Number(
      payload?.E || existingTicker.timestamp || Date.now(),
    );

    futurePriceRef.current[symbol] = {
      ...existingTicker,
      symbol,
      midprice:
        midprice || existingTicker.midprice || existingTicker.markPrice || 0,
      markPrice: markPrice || midprice || existingTicker.markPrice || 0,
      bid: bid || existingTicker.bid || midprice || 0,
      ask: ask || existingTicker.ask || midprice || 0,
      spread,
      timestamp,
    };

    $(`.spreadchart-${symbol}`).text(formatMarketValue(spread));
    $(`.buybtn-${symbol}`).text(formatMarketValue(displayAsk));
    $(`.sellbtn-${symbol}`).text(formatMarketValue(displayBid));
  };

  const getFutureTicker = (symbol = "") => {
    const normalizedSymbol = normalizeFutureSymbol(symbol);
    if (!normalizedSymbol) return null;

    return futurePriceRef.current[normalizedSymbol] || null;
  };

  const subscribePairs = (pairSymbols) => {
    if (!pairSymbols.length) return undefined;

    let pingInterval = null;
    let socket = null;

    try {
      const topics = pairSymbols.map(
        (symbol) => `${normalizeFutureSymbol(symbol).toLowerCase()}@bookTicker`,
      );
      socket = new WebSocket(
        `wss://fstream.binance.com/stream?streams=${topics.join("/")}`,
      );

      futureSocketRef.current = socket;

      socket.onopen = () => {
        console.log("✅ Connected to Binance Future WS");
      };

      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          upsertTicker(msg);
        } catch (e) {
          console.log("Parse error:", e);
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket Error in Future Context:", error);
      };

      socket.onclose = (event) => {
        console.log("WebSocket Closed in Future Context", event);

        if (pingInterval) clearInterval(pingInterval);
        if (futureSocketRef.current === socket) {
          futureSocketRef.current = null;
        }
      };
    } catch (error) {
      console.log("Subscribe error:", error);
    }

    return () => {
      if (pingInterval) {
        clearInterval(pingInterval);
      }

      if (socket) {
        try {
          socket.close();
        } catch (error) {
          console.log("Future socket close error:", error);
        }
      }

      if (futureSocketRef.current === socket) {
        futureSocketRef.current = null;
      }
    };
  };

  useEffect(() => {
    getSymbols();
  }, []);

  useEffect(() => {
    if (!symbols.length) return undefined;

    const cleanup = subscribePairs(symbols);
    return cleanup;
  }, [symbols]);

  // 🟢 Store line refs
  const tpSlShapes = useRef({ tp: null, sl: null, bet: null });

  // 🟩 Register a chart instance when it mounts
  const futureRegisterChart = (chartId, widget) => {
    tvWidgetRef.current[chartId] = widget;
    console.log(chartId, "handleRemoveAllLines");
  };

  // 🟩 Register line references for that chart
  const futureRegisterLines = (chartId, lines) => {
    lineRefs.current[chartId] = lines;
  };

  // 🟩 Unregister (cleanup) when chart unmounts
  const futureUnregisterChart = (chartId) => {
    delete tvWidgetRef.current[chartId];
    delete lineRefs.current[chartId];
  };
  // 🟩 Remove only lines for a specific chart
  const handleFutureRemoveAllLines = (chartId) => {
    const widget = tvWidgetRef.current[chartId];

    if (!widget) {
      console.warn(`⚠️ No widget found for chart ${chartId}`);
      return;
    }

    widget.onChartReady(() => {
      const chart = widget.activeChart();
      const shapes = chart.getAllShapes()?.map((shape) => shape?.id);
      try {
        chart.removeAllShapes(); // 🧹 removes TP/SL/Bet lines for that chart only
        shapes.forEach((shape) => chart.removeEntity(shape));
        console.log(
          `🗑️ Removed all lines for chart ${chartId}`,
          chart.getAllShapes(),
          shapes,
        );
      } catch (err) {
        console.warn(`❌ Failed to remove shapes for chart ${chartId}`, err);
      }

      // clean stored refs for this chart
      delete lineRefs.current[chartId];
    });
  };

  return (
    <FutureContext.Provider
      value={{
        socket: futureSocketRef.current,
        futurePriceRef,
        getFutureTicker,
        futureTvWidgetRef: tvWidgetRef,
        futureTpslShapes: tpSlShapes,
        futureLinesRef: lineRefs,
        futureRegisterLines,
        futureUnregisterChart,
        futureRegisterChart,
        handleFutureRemoveAllLines,
      }}
    >
      {children}
    </FutureContext.Provider>
  );
};

export const useFutureData = () => {
  const context = useContext(FutureContext);
  if (!context)
    throw new Error("useFutureData must be used within a FutureProvider");
  return context;
};

export default FutureProvider;
